import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box} from '@mui/material';
import {t} from 'i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router';
import {useQueryClient} from '@tanstack/react-query';
import './ProtocolLanding.scss';
import ProtocolHeader, {
  ProtocolHeaderLocationState,
} from '../common/protocolIntelligenceV2/header/ProtocolHeader';
import ProtocolSection from './protocol-section/ProtocolSection';
import {
  PROTOCOL_VERSION_DETAILS_QUERY_KEY,
  PROTOCOL_VERSIONS_QUERY_KEY,
  useGetProtocolVersionDetails,
  useGetProtocolVersions,
} from '../../../../hooks/ai/review/useReview';
import {
  ProtocolFormData,
  ProtocolStatus,
  ProtocolVersionSummary,
  hideToast,
  initializeNewProtocol,
  resetProtocol,
  setInitialProtocol,
  setProtocolIds,
  setProtocolVersions,
  showToast,
} from '../../../../redux/slice/aiReviewSliceV2';
import {RootState} from '../../../../redux/store';
import {ToastType} from '../../../../shared/constants';
import {getInAppNavigationPath} from '../common/protocolIntelligenceV2/header/exit-protocol-modal/protocolExitNavigation';
import {
  GetProtocolVersionsResponse,
  ProtocolIntelligenceNavState,
  ProtocolVersionHistoryItem,
} from '../../../../api/types/aiReview.type';
import {
  mapProtocolVersionDetailsToFormData,
  shouldShowProtocolVersionControls,
  cloneProtocolFormData,
  computeNextVersionName,
  areProtocolFormsEqual,
  UNSAVED_REVISION_VERSION_ID,
} from './utils/protocolVersionPayload';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import VersionHistoryPanel from '../header/version-history/VersionHistoryPanel';
import ReviseProtocolModal from './revise-protocol-modal/ReviseProtocolModal';
import DiscardRevisionModal from './discard-revision-modal/DiscardRevisionModal';
import {PROTOCOL_INTELLIGENCE_ROUTE} from '../utils';

const resolveStatus = (state: ProtocolHeaderLocationState): ProtocolStatus => {
  if (state && typeof state === 'object' && state.status) {
    const normalized = String(state.status).toLowerCase();
    if (normalized === 'approved' || normalized === 'draft') {
      return normalized;
    }
  }
  return 'draft';
};

const resolveLatestVersion = (
  state: ProtocolHeaderLocationState
): string | undefined => {
  if (state && typeof state === 'object') {
    return state.latestVersion;
  }
  return undefined;
};

const resolveVersions = (
  state: ProtocolHeaderLocationState
): ProtocolVersionSummary[] | undefined => {
  if (state && typeof state === 'object') {
    return state.versions;
  }
  return undefined;
};

const resolveVersionId = (
  state: ProtocolHeaderLocationState
): number | undefined => {
  if (state && typeof state === 'object') {
    return state.protocolVersionId ?? state.versionId;
  }
  return undefined;
};

const resolveNavState = (
  state: unknown
): ProtocolIntelligenceNavState | null => {
  if (!state) {
    return null;
  }
  if (typeof state === 'string') {
    return {protocolName: state};
  }
  if (typeof state === 'object') {
    return state as ProtocolIntelligenceNavState;
  }
  return null;
};

export const ProtocolLanding = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [pendingVersionId, setPendingVersionId] = useState<number | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<
    number | undefined
  >(undefined);
  const [viewStatus, setViewStatus] = useState<ProtocolStatus | undefined>(
    undefined
  );
  const [showReviseModal, setShowReviseModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [isUnsavedRevision, setIsUnsavedRevision] = useState(false);
  const [priorApprovedVersionId, setPriorApprovedVersionId] = useState<
    number | null
  >(null);
  const [optimisticVersion, setOptimisticVersion] =
    useState<ProtocolVersionSummary | null>(null);
  const [optimisticHistoryVersion, setOptimisticHistoryVersion] =
    useState<ProtocolVersionSummary | null>(null);
  const [caseContextFocusRequest, setCaseContextFocusRequest] = useState(0);
  const [isHistoryVersionSelection, setIsHistoryVersionSelection] =
    useState(false);
  const [isUploadingProtocol, setIsUploadingProtocol] = useState(false);
  /** Set immediately on dropdown / Version History select; cleared when UI hydrates. */
  const [pendingLoadVersionId, setPendingLoadVersionId] = useState<
    number | null
  >(null);

  const revisionBaselineRef = useRef<ProtocolFormData | null>(null);
  const persistedProtocolIdsRef = useRef({
    protocolId: undefined as number | undefined,
    protocolVersionId: undefined as number | undefined,
  });
  const lastAppliedLocationKeyRef = useRef<string | null>(null);
  /** Version last written into Redux from GET. Used to ignore stale cache during a save refetch (AVR-24119). */
  const lastHydratedVersionIdRef = useRef<number | null>(null);
  const lastInsideTargetRef = useRef<HTMLElement | null>(null);
  const pendingVersionIdRef = useRef<number | null>(null);
  const showExitModalRef = useRef(false);
  const exitModeRef = useRef<'none' | 'versionSwitch' | 'pageLeave'>('none');
  const [exitMode, setExitMode] = useState<
    'none' | 'versionSwitch' | 'pageLeave'
  >('none');

  const updatePendingVersionId = useCallback((id: number | null) => {
    pendingVersionIdRef.current = id;
    setPendingVersionId(id);
  }, []);

  const setExitModalOpen = useCallback((open: boolean) => {
    showExitModalRef.current = open;
    setShowExitModal(open);
  }, []);

  const beginVersionSwitch = useCallback(
    (nextVersionId: number) => {
      exitModeRef.current = 'versionSwitch';
      setExitMode('versionSwitch');
      updatePendingVersionId(nextVersionId);
      setPendingPath(null);
      setExitModalOpen(true);
    },
    [setExitModalOpen, updatePendingVersionId]
  );

  const beginPageLeave = useCallback(
    (path: string | null) => {
      if (exitModeRef.current === 'versionSwitch' || showExitModalRef.current) {
        return;
      }
      exitModeRef.current = 'pageLeave';
      setExitMode('pageLeave');
      updatePendingVersionId(null);
      setPendingPath(path);
      setExitModalOpen(true);
    },
    [setExitModalOpen, updatePendingVersionId]
  );

  const cancelExitModal = useCallback(() => {
    exitModeRef.current = 'none';
    setExitMode('none');
    updatePendingVersionId(null);
    setPendingPath(null);
    setExitModalOpen(false);
  }, [setExitModalOpen, updatePendingVersionId]);

  const protocol = useSelector((state: RootState) => state.aiReviewV2.protocol);
  const initialProtocol = useSelector(
    (state: RootState) => state.aiReviewV2.initialProtocol
  );
  const toast = useSelector((state: RootState) => state.aiReviewV2.toast);
  const storedVersions = useSelector(
    (state: RootState) => state.aiReviewV2.protocolVersions.versions
  );
  const locationState = location.state as ProtocolHeaderLocationState;
  const locationStatus = resolveStatus(locationState);
  const hasUnsavedChanges = !areProtocolFormsEqual(protocol, initialProtocol);

  const handleToastClose = useCallback(() => {
    dispatch(hideToast());
  }, [dispatch]);

  const isNameOnlyCreateNavigation = typeof location.state === 'string';
  const isFreshCreateFlow =
    isNameOnlyCreateNavigation && protocol.protocolId == null;

  persistedProtocolIdsRef.current = {
    protocolId: protocol.protocolId,
    protocolVersionId: protocol.protocolVersionId,
  };

  const syncLocationVersionState = useCallback(
    (
      data: {protocolId: number; versionId: number},
      nextStatus: ProtocolStatus
    ) => {
      const currentState = location.state;
      if (currentState && typeof currentState === 'object') {
        navigate(location.pathname, {
          replace: true,
          state: {
            ...(currentState as Record<string, unknown>),
            protocolId: data.protocolId,
            versionId: data.versionId,
            protocolVersionId: data.versionId,
            status: nextStatus,
          },
        });
        return;
      }

      navigate(PROTOCOL_INTELLIGENCE_ROUTE, {
        replace: true,
        state: {
          protocolName: protocol.protocolName,
          protocolId: data.protocolId,
          versionId: data.versionId,
          status: nextStatus,
        },
      });
    },
    [location.pathname, location.state, navigate, protocol.protocolName]
  );

  /**
   * Same route is reused for create vs edit, so Landing often does not remount.
   * Reset local version/view state whenever we navigate into a fresh create
   * (string location.state) or into an existing protocol (object location.state).
   */
  useEffect(() => {
    if (typeof location.state !== 'string') {
      return;
    }

    const name = location.state.trim();
    if (!name) {
      return;
    }

    const {protocolId, protocolVersionId} = persistedProtocolIdsRef.current;
    if (protocolId != null && protocolVersionId != null) {
      return;
    }

    dispatch(initializeNewProtocol(name));
    setSelectedVersionId(undefined);
    setViewStatus('draft');
    setIsUnsavedRevision(false);
    setOptimisticVersion(null);
    setOptimisticHistoryVersion(null);
    setIsHistoryVersionSelection(false);
    setPriorApprovedVersionId(null);
    revisionBaselineRef.current = null;
    setIsVersionHistoryOpen(false);
    setShowExitModal(false);
    setPendingPath(null);
    setPendingVersionId(null);
    pendingVersionIdRef.current = null;
    exitModeRef.current = 'none';
    setExitMode('none');
    showExitModalRef.current = false;
  }, [dispatch, location.key, location.state]);

  useEffect(() => {
    if (!location.state || typeof location.state !== 'object') {
      return;
    }

    if (lastAppliedLocationKeyRef.current === location.key) {
      return;
    }
    lastAppliedLocationKeyRef.current = location.key;

    const nextVersionId =
      resolveVersionId(location.state as ProtocolHeaderLocationState) ??
      (location.state as ProtocolIntelligenceNavState).versionId;
    if (nextVersionId != null) {
      setSelectedVersionId(nextVersionId);
    }
    setViewStatus(resolveStatus(location.state as ProtocolHeaderLocationState));
    setIsUnsavedRevision(false);
    setOptimisticVersion(null);
    setOptimisticHistoryVersion(null);
    setIsHistoryVersionSelection(false);
    setPriorApprovedVersionId(null);
    revisionBaselineRef.current = null;
  }, [location.key, location.state]);

  useEffect(() => {
    if (!hasUnsavedChanges || showExitModal || isUploadingProtocol) return;

    const handleOutsideInteraction = (e: MouseEvent) => {
      if (e.button === 2) return;
      if (showExitModalRef.current) return;

      const wizardEl = document.querySelector(
        '.protocol-intelligence-container'
      );
      if (!wizardEl) return;

      const target = e.target as HTMLElement;

      if (wizardEl.contains(target)) {
        lastInsideTargetRef.current = target;
        return;
      }

      // Overlay controls stay in the wizard tree, but treat them as in-page
      // even if a portal or stacking context moves the click target to body.
      if (target.closest('.back-to-top-button')) {
        lastInsideTargetRef.current = target;
        return;
      }

      const versionMenuItem = target.closest(
        '[data-testid^="arr-single-select-dropdown-menu-item-"]'
      );
      if (versionMenuItem) {
        const rawId = versionMenuItem
          .getAttribute('data-testid')
          ?.replace('arr-single-select-dropdown-menu-item-', '');
        const nextVersionId = Number(rawId);
        if (Number.isFinite(nextVersionId)) {
          // Remember the pick; do not open the modal here. Opening a Dialog
          // on mousedown while the version menu is still open races MUI and
          // reopens this confirmation as a page-leave (AVR-22636).
          setPendingPath(null);
          updatePendingVersionId(nextVersionId);
        }
        return;
      }

      const isNavChrome = Boolean(
        target.closest('.navigation-sidebar') ||
          target.closest('.navbar-submenu') ||
          target.closest('.arr-menu') ||
          target.closest('[data-nav-href]')
      );

      const isMuiPortal = Boolean(
        target.closest('[role="listbox"]') ||
          target.closest('[role="option"]') ||
          target.closest('.MuiPopover-root') ||
          target.closest('.MuiMenu-root') ||
          target.closest('.MuiMenuItem-root') ||
          target.closest('.arr-dropdown-container__arr-dropdown-menu') ||
          target.closest('[role="dialog"]') ||
          target.closest('.modal') ||
          target.closest('.MuiDialog-root') ||
          target.closest('.MuiBackdrop-root') ||
          target.classList.contains('MuiBackdrop-root')
      );

      // Skip in-page MUI portals (version dropdown, tab popovers). Navbar
      // menus are isNavChrome and must still open the leave-protocol modal.
      if (!isNavChrome && isMuiPortal) {
        return;
      }

      const navigationPath = getInAppNavigationPath(target);

      // Do not change shared Navbar. Protocol page only: allow submenu expand,
      // but block leaf left-nav clicks (programmatic navigate) and show leave modal.
      const isNavSubmenuExpandOnly = Boolean(
        isNavChrome &&
          !navigationPath &&
          target.closest('.nav-item[aria-haspopup="menu"]')
      );
      if (isNavSubmenuExpandOnly) {
        return;
      }

      lastInsideTargetRef.current = null;
      // Leftover clicks after the version menu closes have no href. Stealing
      // them wipes the pending version and Discard then goes to the list.
      if (!navigationPath && !isNavChrome && e.type === 'click') {
        return;
      }
      if (
        !navigationPath &&
        (exitModeRef.current === 'versionSwitch' ||
          pendingVersionIdRef.current != null)
      ) {
        return;
      }
      if (navigationPath) {
        updatePendingVersionId(null);
      }
      e.stopPropagation();
      e.preventDefault();
      // Left-nav leaves often have no DOM href; treat like Exit (pendingPath null).
      beginPageLeave(navigationPath);
    };

    document.addEventListener('mousedown', handleOutsideInteraction, true);
    // Capture click as well so role=button navigate() cannot race past the guard
    document.addEventListener('click', handleOutsideInteraction, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction, true);
      document.removeEventListener('click', handleOutsideInteraction, true);
    };
  }, [
    beginPageLeave,
    hasUnsavedChanges,
    showExitModal,
    isUploadingProtocol,
    updatePendingVersionId,
  ]);

  const navState = useMemo(
    () => resolveNavState(location.state),
    [location.state]
  );

  const protocolId = navState?.protocolId ?? protocol.protocolId;
  const navVersionId = navState?.versionId ?? resolveVersionId(locationState);

  const {data: versionsResponse} = useGetProtocolVersions(protocolId);

  const versions: ProtocolVersionSummary[] = useMemo(() => {
    let base: ProtocolVersionSummary[] = [];
    if (versionsResponse?.versions?.length) {
      base = versionsResponse.versions;
    } else if (storedVersions.length) {
      base = storedVersions;
    } else {
      base = resolveVersions(locationState) ?? [];
    }

    const localVersions = [optimisticVersion, optimisticHistoryVersion].filter(
      (version): version is ProtocolVersionSummary =>
        version != null && !base.some((v) => v.versionId === version.versionId)
    );

    if (localVersions.length) {
      return [...localVersions, ...base];
    }
    return base;
  }, [
    versionsResponse,
    storedVersions,
    locationState,
    optimisticHistoryVersion,
    optimisticVersion,
  ]);

  const latestVersionFromNav =
    resolveLatestVersion(locationState) ??
    (navState as ProtocolIntelligenceNavState | null)?.latestVersion;

  useEffect(() => {
    if (!protocolId || !versionsResponse?.versions) {
      return;
    }
    dispatch(
      setProtocolVersions({
        protocolId,
        versions: versionsResponse.versions,
      })
    );
  }, [dispatch, protocolId, versionsResponse]);

  useEffect(() => {
    if (
      optimisticVersion &&
      versionsResponse?.versions?.some(
        (v) => v.versionId === optimisticVersion.versionId
      )
    ) {
      setOptimisticVersion(null);
    }
  }, [optimisticVersion, versionsResponse]);

  useEffect(() => {
    if (
      optimisticHistoryVersion &&
      versionsResponse?.versions?.some(
        (v) => v.versionId === optimisticHistoryVersion.versionId
      )
    ) {
      setOptimisticHistoryVersion(null);
    }
  }, [optimisticHistoryVersion, versionsResponse]);

  useEffect(() => {
    if (selectedVersionId != null || isFreshCreateFlow) {
      return;
    }
    const initialId = navVersionId ?? versions[0]?.versionId;
    if (initialId != null) {
      setSelectedVersionId(initialId);
    }
  }, [navVersionId, versions, selectedVersionId, isFreshCreateFlow]);

  useEffect(() => {
    if (selectedVersionId == null) {
      return;
    }
    if (isUnsavedRevision) {
      return;
    }
    // Do not flip approved→draft (edit-mode remount) until the new version
    // details request has started and hydrated — otherwise the main thread can
    // block for seconds before the details API is sent.
    if (pendingLoadVersionId != null) {
      return;
    }
    const selected = versions.find((v) => v.versionId === selectedVersionId);
    if (selected) {
      setViewStatus(selected.status);
    }
  }, [
    selectedVersionId,
    versions,
    isUnsavedRevision,
    pendingLoadVersionId,
  ]);

  const status = viewStatus ?? locationStatus;
  const versionId = selectedVersionId;
  const showVersionControls = shouldShowProtocolVersionControls(
    latestVersionFromNav,
    versions.length,
    status
  );

  const versionDetailsEnabled =
    !isHistoryVersionSelection &&
    !isFreshCreateFlow &&
    !isUnsavedRevision &&
    versionId != null &&
    versionId !== UNSAVED_REVISION_VERSION_ID;

  const {data, isLoading, isFetching, isError} = useGetProtocolVersionDetails(
    versionId,
    versionDetailsEnabled
  );

  // Show loader from the moment the user switches version until that version
  // is applied to Redux/UI. Avoid tying to bare isFetching (background refetch
  // after save would flash the overlay).
  const isVersionDetailsLoading =
    pendingLoadVersionId != null || (versionDetailsEnabled && isLoading);

  useEffect(() => {
    if (
      !isError ||
      isHistoryVersionSelection ||
      isUnsavedRevision ||
      isFreshCreateFlow ||
      versionId == null ||
      versionId === UNSAVED_REVISION_VERSION_ID
    ) {
      return;
    }

    setPendingLoadVersionId(null);
    dispatch(
      showToast({
        title: t('common.error'),
        message: t(
          'ai.review.protocolIntelligence.getVersionDetailsErrorToast'
        ),
        status: [ToastType.Error],
      })
    );
  }, [
    dispatch,
    isError,
    isFreshCreateFlow,
    isHistoryVersionSelection,
    isUnsavedRevision,
    versionId,
  ]);

  const applyVersionSwitch = useCallback(
    (nextVersionId: number) => {
      setIsHistoryVersionSelection(false);
      setPendingLoadVersionId(nextVersionId);
      setSelectedVersionId(nextVersionId);
      setIsVersionHistoryOpen(false);
      updatePendingVersionId(null);
      setExitModalOpen(false);
      setPendingPath(null);
      exitModeRef.current = 'none';
      setExitMode('none');

      if (protocolId != null) {
        const selected = versions.find(
          (version) => version.versionId === nextVersionId
        );
        syncLocationVersionState(
          {protocolId, versionId: nextVersionId},
          (selected?.status ?? protocol.status ?? 'draft') as ProtocolStatus
        );
      }
    },
    [
      protocol.status,
      protocolId,
      setExitModalOpen,
      syncLocationVersionState,
      updatePendingVersionId,
      versions,
    ]
  );

  const clearUnsavedRevision = useCallback(() => {
    setIsUnsavedRevision(false);
    setPriorApprovedVersionId(null);
    setOptimisticVersion(null);
    setPendingLoadVersionId(null);
    revisionBaselineRef.current = null;
  }, []);

  const handleVersionHistorySelect = useCallback(
    (entry: ProtocolVersionHistoryItem) => {
      // Selecting from Version History must load that version's details (same as
      // dropdown switch). Never leave isHistoryVersionSelection=true — that flag
      // disables useGetProtocolVersionDetails and leaves stale form/version ids
      // (AVR-23048).
      // Close drawer immediately; full-screen loader covers until UI hydrates.
      clearUnsavedRevision();
      setIsHistoryVersionSelection(false);
      setPendingLoadVersionId(entry.versionId);
      setIsVersionHistoryOpen(false);

      const selectedVersion = versions.find(
        (version) => version.versionId === entry.versionId
      );
      if (optimisticHistoryVersion?.versionId !== entry.versionId) {
        setOptimisticHistoryVersion(
          selectedVersion
            ? null
            : {
                versionId: entry.versionId,
                name: entry.versionName,
                status: entry.status,
              }
        );
      }

      setSelectedVersionId(entry.versionId);
      // Defer setViewStatus until hydrate so draft edit-mode remount does not
      // block the details API from starting.
      updatePendingVersionId(null);
      setExitModalOpen(false);
      setPendingPath(null);
      exitModeRef.current = 'none';
      setExitMode('none');
      setCaseContextFocusRequest((request) => request + 1);
    },
    [
      clearUnsavedRevision,
      optimisticHistoryVersion?.versionId,
      setExitModalOpen,
      updatePendingVersionId,
      versions,
    ]
  );

  const restoreApprovedRevision = useCallback(() => {
    const baseline = revisionBaselineRef.current;
    const priorId = priorApprovedVersionId;
    if (baseline) {
      dispatch(setInitialProtocol(cloneProtocolFormData(baseline)));
    }
    if (priorId != null) {
      applyVersionSwitch(priorId);
    }
    clearUnsavedRevision();
  }, [
    applyVersionSwitch,
    clearUnsavedRevision,
    dispatch,
    priorApprovedVersionId,
  ]);

  const handleVersionChange = useCallback(
    (nextVersionId: number) => {
      if (nextVersionId === selectedVersionId) {
        return;
      }
      if (hasUnsavedChanges) {
        beginVersionSwitch(nextVersionId);
        return;
      }
      applyVersionSwitch(nextVersionId);
    },
    [
      applyVersionSwitch,
      beginVersionSwitch,
      hasUnsavedChanges,
      selectedVersionId,
    ]
  );

  const handleExitDiscard = useCallback(
    (versionId?: number | null) => {
      const fromArg = typeof versionId === 'number' ? versionId : null;
      const nextVersionId =
        fromArg ?? pendingVersionIdRef.current ?? pendingVersionId;
      if (nextVersionId != null) {
        applyVersionSwitch(nextVersionId);
      }
    },
    [applyVersionSwitch, pendingVersionId]
  );

  const handleExitSaveDraft = useCallback(
    (versionId?: number | null) => {
      const fromArg = typeof versionId === 'number' ? versionId : null;
      const nextVersionId =
        fromArg ?? pendingVersionIdRef.current ?? pendingVersionId;
      if (nextVersionId != null) {
        applyVersionSwitch(nextVersionId);
      }
    },
    [applyVersionSwitch, pendingVersionId]
  );

  const handleSetShowExitModal = useCallback(
    (open: boolean) => {
      if (!open) {
        cancelExitModal();
        return;
      }
      beginPageLeave(null);
    },
    [beginPageLeave, cancelExitModal]
  );

  const handleReviseClick = useCallback(() => {
    setShowReviseModal(true);
  }, []);

  /** Local-only: copy tab data into draft edit; POST deferred to Save Draft / Approve. */
  const handleCreateNewVersion = useCallback(() => {
    if (selectedVersionId == null) {
      return;
    }

    const parentVersionId = selectedVersionId;
    const baseline = cloneProtocolFormData(protocol);
    const draftCopy: ProtocolFormData = {
      ...cloneProtocolFormData(protocol),
      status: 'draft',
    };
    const nextName = computeNextVersionName(versions);

    revisionBaselineRef.current = baseline;
    setPriorApprovedVersionId(parentVersionId);
    setIsUnsavedRevision(true);
    setIsHistoryVersionSelection(false);
    setPendingLoadVersionId(null);
    setOptimisticVersion({
      versionId: UNSAVED_REVISION_VERSION_ID,
      name: nextName,
      status: 'draft',
    });
    setSelectedVersionId(UNSAVED_REVISION_VERSION_ID);
    setViewStatus('draft');
    dispatch(setInitialProtocol(draftCopy));
    setIsVersionHistoryOpen(false);
    setShowReviseModal(false);
  }, [dispatch, protocol, selectedVersionId, versions]);

  const handleDiscardRevision = useCallback(() => {
    restoreApprovedRevision();
    setShowDiscardModal(false);
  }, [restoreApprovedRevision]);

  const handleRevisionPersisted = useCallback(
    (
      data: {protocolId: number; versionId: number},
      nextStatus: ProtocolStatus
    ) => {
      setSelectedVersionId(data.versionId);
      setViewStatus(nextStatus);
      setOptimisticVersion(null);
      setOptimisticHistoryVersion(null);
      setIsHistoryVersionSelection(false);
      setPendingLoadVersionId(null);

      const withoutUnsaved = versions.filter(
        (version) => version.versionId !== UNSAVED_REVISION_VERSION_ID
      );
      const hasVersion = withoutUnsaved.some(
        (version) => version.versionId === data.versionId
      );
      const nextVersions: ProtocolVersionSummary[] = hasVersion
        ? withoutUnsaved.map((version) =>
            version.versionId === data.versionId
              ? {...version, status: nextStatus}
              : version
          )
        : [
            {
              versionId: data.versionId,
              name: computeNextVersionName(withoutUnsaved),
              status: nextStatus,
            },
            ...withoutUnsaved,
          ];

      dispatch(
        setProtocolVersions({
          protocolId: data.protocolId,
          versions: nextVersions,
        })
      );

      queryClient.setQueryData<GetProtocolVersionsResponse>(
        [...PROTOCOL_VERSIONS_QUERY_KEY, data.protocolId],
        (current) => ({
          ...(current ?? {versions: []}),
          versions: nextVersions,
        })
      );

      clearUnsavedRevision();

      syncLocationVersionState(data, nextStatus);

      void queryClient.invalidateQueries({
        queryKey: [...PROTOCOL_VERSION_DETAILS_QUERY_KEY, data.versionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [...PROTOCOL_VERSIONS_QUERY_KEY, data.protocolId],
      });
    },
    [clearUnsavedRevision, dispatch, queryClient, syncLocationVersionState, versions]
  );

  const handleRevisionCreateFailed = useCallback(() => {
    restoreApprovedRevision();
  }, [restoreApprovedRevision]);

  useEffect(() => {
    return () => {
      dispatch(resetProtocol());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!data || !versionId || isUnsavedRevision || isFreshCreateFlow) {
      return;
    }

    // Keep overlay up through the network round-trip for a user-initiated switch.
    if (pendingLoadVersionId === versionId && isFetching) {
      return;
    }

    // AVR-24119: after save, invalidateQueries refetches while `data` is still the
    // previous GET. Re-applying that cache overwrites in-memory upload/edits.
    // First load still hydrates (ref is null). Version switch uses pendingLoadVersionId.
    if (
      isFetching &&
      pendingLoadVersionId == null &&
      lastHydratedVersionIdRef.current === versionId
    ) {
      return;
    }

    const formData = mapProtocolVersionDetailsToFormData(data, {
      protocolId: protocolId ?? protocol.protocolId,
      protocolVersionId: versionId ?? protocol.protocolVersionId,
    });
    const hydratedProtocol: ProtocolFormData = {
      ...formData,
      protocolId: formData.protocolId ?? protocol.protocolId,
      protocolVersionId:
        formData.protocolVersionId ?? protocol.protocolVersionId ?? versionId,
    };
    dispatch(setInitialProtocol(hydratedProtocol));
    const resolvedProtocolId = hydratedProtocol.protocolId;
    const resolvedVersionId = hydratedProtocol.protocolVersionId;
    if (resolvedProtocolId != null && resolvedVersionId != null) {
      dispatch(
        setProtocolIds({
          protocolId: resolvedProtocolId,
          protocolVersionId: resolvedVersionId,
        })
      );
    }
    const nextStatus = String(formData.status ?? '')
      .trim()
      .toLowerCase();
    if (nextStatus === 'approved' || nextStatus === 'draft') {
      setViewStatus(nextStatus);
    }
    lastHydratedVersionIdRef.current = versionId;
    setPendingLoadVersionId((pending) =>
      pending == null || pending === versionId ? null : pending
    );
  }, [
    data,
    dispatch,
    isFetching,
    isFreshCreateFlow,
    isUnsavedRevision,
    pendingLoadVersionId,
    protocol.protocolId,
    protocol.protocolVersionId,
    protocolId,
    versionId,
  ]);

  return (
    <Box
      className="protocol-intelligence-container"
      data-testid="protocol-intelligence-container"
    >
      <Box className="protocol-intelligence-container__header">
        <ProtocolHeader
          showExitModal={showExitModal}
          setShowExitModal={handleSetShowExitModal}
          pendingPath={pendingPath}
          setPendingPath={setPendingPath}
          pendingSwitchVersionId={
            exitMode === 'versionSwitch'
              ? (pendingVersionId ?? pendingVersionIdRef.current)
              : null
          }
          onExitDiscard={handleExitDiscard}
          onExitSaveDraft={handleExitSaveDraft}
          status={status}
          isSavingDisabled={
            isVersionDetailsLoading || isUploadingProtocol
          }
          showVersionControls={showVersionControls}
          onVersionHistoryClick={() => setIsVersionHistoryOpen(true)}
          onReviseClick={handleReviseClick}
          isUnsavedRevision={isUnsavedRevision}
          revisionParentVersionId={priorApprovedVersionId}
          onUnsavedRevisionCancel={() => setShowDiscardModal(true)}
          onRevisionPersisted={handleRevisionPersisted}
          onRevisionCreateFailed={handleRevisionCreateFailed}
        />
      </Box>
      <Box
        className="protocol-intelligence-container__section"
        component="section"
        aria-label={t('ai.review.protocolIntelligence.protocolContentLabel')}
      >
        {isError && !isUnsavedRevision ? (
          <Box data-testid="protocol-version-load-error">
            Unable to load protocol details.
          </Box>
        ) : (
          <ProtocolSection
            status={status}
            versions={versions}
            selectedVersionId={selectedVersionId}
            onVersionChange={handleVersionChange}
            focusCaseContextRequest={caseContextFocusRequest}
            showVersionControls={showVersionControls}
            isVersionDropdownDisabled={
              isUnsavedRevision || isVersionDetailsLoading
            }
            isUploadingProtocol={isUploadingProtocol}
            setIsUploadingProtocol={setIsUploadingProtocol}
          />
        )}
      </Box>
      <ArrLoadingSpinner
        overlay
        isOpen={isVersionDetailsLoading}
        show={isVersionDetailsLoading}
        ariaLabel={t('common.loading')}
      />
      <VersionHistoryPanel
        isOpen={isVersionHistoryOpen}
        onClose={() => setIsVersionHistoryOpen(false)}
        protocolId={protocolId}
        onVersionSelect={handleVersionHistorySelect}
      />
      {toast?.open && (
        <ArrToastNotification
          title={toast.title}
          message={toast.message}
          status={toast.status}
          onClose={handleToastClose}
          data-testid="protocol-intelligence-toast"
          aria-label={toast.title || toast.message}
        />
      )}
      <ReviseProtocolModal
        open={showReviseModal}
        onClose={() => setShowReviseModal(false)}
        onConfirm={handleCreateNewVersion}
      />
      <DiscardRevisionModal
        open={showDiscardModal}
        onClose={() => setShowDiscardModal(false)}
        onConfirm={handleDiscardRevision}
      />
    </Box>
  );
};

export default ProtocolLanding;
